/* ============================================================
   Alatreyu PWYC — JavaScript
   Modal open/close, custom dropdown, qty selector,
   Cart API integration, cost popup animation,
   same-page format switching (candle ↔ wax melts).
   ============================================================ */

(function () {
  'use strict';

  /* ── State ── */
  var state = {
    selectedVariantId: null,
    selectedPrice: '',
    selectedPriceFormatted: '',
    quantity: 1,
    productData: null,
    retailVariantId: null,
    retailPrice: 0,
    /* Format switching */
    siblingsData: null,
    currentHandle: null,
    /* PWYC behavior analytics */
    pageLoadTime: Date.now(),
    tierChangeCount: 0,
    sawCostBreakdown: false
  };

  var STOREFRONT_API = 'https://front.4147r3yu.com';

  /* ── Focus Trap utility ── */
  var FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
  function trapFocus(container) {
    function handler(e) {
      if (e.key !== 'Tab') return;
      var focusable = Array.prototype.slice.call(container.querySelectorAll(FOCUSABLE)).filter(function (el) {
        return el.offsetParent !== null;
      });
      if (!focusable.length) return;
      var first = focusable[0];
      var last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
    container.addEventListener('keydown', handler);
    return function () { container.removeEventListener('keydown', handler); };
  }

  /* ── Escape HTML special characters to prevent XSS ── */
  function escapeHtml(str) {
    if (typeof str !== 'string') return '';
    return str
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  document.addEventListener('DOMContentLoaded', function () {
    loadProductData();
    loadSiblingsData();
    fetchLiveCostBreakdown();
    initPWYCTrigger();
    initPWYCModal();
    initPWYCDropdown();
    initQtySelector();
    initAddToCart();
    initCostModal();
    initCostPopups();
    initMobileSheet();
    initFormatSwitcher();
    initRatingScroll();
    formatSoldCount();
  });

  /* ── Format sold count with commas ── */
  function formatSoldCount() {
    var el = document.getElementById('product-sold');
    if (!el) return;
    var raw = parseInt(el.getAttribute('data-sold'), 10);
    if (raw > 0) {
      el.textContent = '\u00b7 ' + raw.toLocaleString() + ' sold';
    }
  }

  /* ── Helper: format dropdown label ── */
  function dropdownLabel(priceFormatted, variantId) {
    if (variantId === state.retailVariantId) {
      return priceFormatted + ' (SUGGESTED PRICE)';
    }
    return priceFormatted;
  }

  /* ── Load product data from JSON block ── */
  function loadProductData() {
    var el = document.getElementById('alatreyu-product-data');
    if (!el) return;
    try {
      state.productData = JSON.parse(el.textContent);
      state.retailVariantId = state.productData.retailVariantId || null;
      state.retailPrice = state.productData.retailPrice || 0;
      state.currentHandle = state.productData.handle || null;

      /* Set initial variant */
      var variants = state.productData.variants || [];
      for (var i = 0; i < variants.length; i++) {
        if (variants[i].available) {
          state.selectedVariantId = variants[i].id;
          state.selectedPrice = variants[i].price;
          state.selectedPriceFormatted = variants[i].priceFormatted;
          break;
        }
      }
    } catch (e) { /* silent */ }
  }

  /* ── Load sibling products data for format switching ── */
  function loadSiblingsData() {
    var el = document.getElementById('alatreyu-siblings-data');
    if (!el) return;
    try {
      state.siblingsData = JSON.parse(el.textContent);
    } catch (e) {
      state.siblingsData = {};
    }

    /* Store current handle in history state for popstate */
    if (state.currentHandle) {
      history.replaceState({ handle: state.currentHandle }, '', window.location.href);
    }
  }

  /* ══════════════════════════════════════════════════════
     FORMAT SWITCHER — Same-page product switching
     ══════════════════════════════════════════════════════ */

  /* ── Rating click → smooth scroll to reviews section ── */
  function initRatingScroll() {
    var ratingEl = document.getElementById('product-rating');
    if (!ratingEl) return;
    ratingEl.addEventListener('click', function (e) {
      e.preventDefault();
      var target = document.getElementById('alatreyu-reviews');
      if (target) {
        target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    });
  }

  /* ── Attach click handlers to format thumbnails ── */
  function initFormatSwitcher() {
    if (!state.siblingsData) return;

    var options = document.querySelectorAll('[data-format-handle]');
    for (var i = 0; i < options.length; i++) {
      options[i].addEventListener('click', function (e) {
        var handle = this.getAttribute('data-format-handle');
        /* Always prevent navigation for known sibling handles (even if already active) */
        if (handle && state.siblingsData && state.siblingsData[handle]) {
          e.preventDefault();
          if (handle !== state.currentHandle) {
            if (window.AlatreyuHaptics) window.AlatreyuHaptics('tap');
            switchProduct(handle);
          }
        }
      });
    }

    /* Handle browser back/forward */
    window.addEventListener('popstate', function (e) {
      if (e.state && e.state.handle && state.siblingsData && state.siblingsData[e.state.handle]) {
        switchProductNoHistory(e.state.handle);
      }
    });
  }

  /* ── Switch to a different product format (with history push) ── */
  function switchProduct(handle) {
    var data = state.siblingsData[handle];
    if (!data) return;

    /* Publish format switch for Custom Pixel analytics */
    var fromFmt = state.currentHandle && state.currentHandle.indexOf('-wax-melts') !== -1 ? 'wax_melt' : 'candle';
    var toFmt = handle.indexOf('-wax-melts') !== -1 ? 'wax_melt' : 'candle';
    if (typeof Shopify !== 'undefined' && Shopify.analytics && Shopify.analytics.publish) {
      Shopify.analytics.publish('format_switched', {
        from_handle: state.currentHandle || '',
        to_handle: handle,
        from_format: fromFmt,
        to_format: toFmt
      });
    }
    /* GA4 format_switched event */
    if (typeof gtag === 'function') {
      gtag('event', 'format_switched', {
        from_format: fromFmt,
        to_format: toFmt,
        from_handle: state.currentHandle || '',
        to_handle: handle
      });
    }

    /* Instant swap — the former fade-out-fade-in was removed 2026-04-11
       because it revealed the full-width video underneath and caused a
       visible flash. Since candle/wax melt share title/scent notes/desc
       and only numeric data swaps, instant is imperceptible. */
    var infoSide = document.querySelector('.product-info-side');
    if (infoSide) infoSide.classList.add('switching');
    applyProductSwitch(data, handle);
    updateURL(data);
    if (infoSide) infoSide.classList.remove('switching');
  }

  /* ── Switch without pushing to history (for popstate) ── */
  function switchProductNoHistory(handle) {
    var data = state.siblingsData[handle];
    if (!data) return;

    /* Instant swap — matches switchProduct() above (2026-04-11) */
    var infoSide = document.querySelector('.product-info-side');
    if (infoSide) infoSide.classList.add('switching');
    applyProductSwitch(data, handle);
    /* Update page title (updateURL is skipped for popstate) */
    if (data.title) {
      document.title = pwycPageTitle(data.title);
    }
    if (infoSide) infoSide.classList.remove('switching');
  }

  /* ── Core swap: updates all state and DOM ── */
  function applyProductSwitch(data, handle) {
    /* Update state */
    state.currentHandle = handle;
    state.productData = {
      productId: data.productId,
      handle: data.handle,
      sku: data.sku,
      title: data.title,
      url: data.url,
      retailVariantId: data.retailVariantId,
      retailPrice: data.retailPrice,
      variants: data.variants,
      avgPricePaid: data.avgPricePaid,
      totalSold: data.totalSold
    };
    state.retailVariantId = data.retailVariantId;
    state.retailPrice = data.retailPrice;

    /* Select first available variant */
    var variants = data.variants || [];
    state.selectedVariantId = null;
    for (var i = 0; i < variants.length; i++) {
      if (variants[i].available) {
        state.selectedVariantId = variants[i].id;
        state.selectedPrice = variants[i].price;
        state.selectedPriceFormatted = variants[i].priceFormatted;
        break;
      }
    }

    /* Toggle the buy button between in-stock and sold-out states.
       A sibling with no available variant leaves selectedVariantId null;
       the CSS [data-available] rules then swap the qty+add controls for
       the disabled "Sold Out" button. Without this, switching between a
       candle and a sold-out wax melt (or vice versa) left the previous
       format's buy state stuck on screen. */
    var actionsEl = document.querySelector('.pwyc-bar__actions');
    if (actionsEl) {
      actionsEl.setAttribute('data-available', state.selectedVariantId ? 'true' : 'false');
    }

    /* Re-point the inline "Notify me" card at the format now on screen.
       The card is rendered once by Liquid carrying the LANDING product's
       sku/handle/id, and the [data-available] flip above is what reveals it.
       Without this, switching from an in-stock candle to its sold-out wax
       melt showed the card still pointed at the candle, so the signup was
       recorded against a product that never left stock (2026-07-25: two such
       rows, C-PETRICHOR + C-PVMT, from a shopper who wanted the melts).
       Candle and wax melt are tracked separately from here on: the card
       keeps per-SKU signup state, so each format can be signed up for
       independently. */
    if (window.AlatreyuNotify && window.AlatreyuNotify.setProduct) {
      window.AlatreyuNotify.setProduct({
        sku: data.sku,
        handle: data.handle || handle,
        productId: data.productId,
        title: data.title
      });
    }

    /* Update all DOM elements */
    updateProductDOM(data);
    /* updateVideo(data) intentionally skipped 2026-04-11:
       Format switching (candle ↔ wax melts) within the same scent family
       shares the same visual theme. Swapping the <video> element causes
       it to rebuild and reload, flashing the dark #1a1a1a container bg
       during the ~300-600ms load window ("dip to black"). Since candle/
       wax melt share title/scent notes/description too (see comment in
       updateProductDOM), the video is the last remaining source of visual
       change during format switching, and keeping it fixed matches the
       "no visible change except the active thumb" UX goal. */
    updatePWYCBar(data);
    updatePWYCModal(data);
    updateMobileSheetVariants(data);
    updateFormatChooser(handle);
    clearCostData();

    /* Fetch fresh cost breakdown for new product */
    fetchLiveCostBreakdown();

    /* Refresh "Similar Scents" section for the new product */
    refreshSimilarScents(data.productId);
  }

  /* ── Update rating, sold count (title/scent/description stay — same scent) ── */
  function updateProductDOM(data) {
    /* Title, scent notes, and description are NOT updated during format switching.
       Candle ↔ Wax Melts share the same scent — only the format, price, and
       stats differ. Keeping the original copy avoids jarring text swaps. */

    /* Rating */
    var rating = document.getElementById('product-rating');
    if (rating) {
      if (data.reviewCount > 0) {
        var ratingText = rating.querySelector('.product-rating__text');
        if (ratingText) {
          ratingText.textContent = data.reviewCount + ' ratings (' + data.reviewAvg + ')';
        }
        rating.style.display = '';
      } else {
        rating.style.display = 'none';
      }
    }

    /* Sold count */
    var soldEl = document.getElementById('product-sold');
    if (soldEl && data.totalSold) {
      soldEl.textContent = '\u00b7 ' + Number(data.totalSold).toLocaleString() + ' sold';
      soldEl.style.display = '';
    } else if (soldEl) {
      soldEl.style.display = 'none';
    }
  }

  /* ── Swap video/image in left panel ── */
  function updateVideo(data) {
    var container = document.getElementById('product-media-container');
    if (!container) return;

    /* Check if media source is unchanged — skip swap entirely */
    var currentSource = container.querySelector('source');
    var currentImg = container.querySelector('img');
    var currentSrc = currentSource ? currentSource.getAttribute('src')
                   : currentImg ? currentImg.getAttribute('src')
                   : null;
    var newSrc = data.videoUrl || data.featuredImage || null;

    if (currentSrc && newSrc && currentSrc === newSrc) {
      return; /* Same media — leave video/image untouched */
    }

    if (data.videoUrl) {
      container.className = 'product-video-bg';
      container.textContent = '';
      var video = document.createElement('video');
      video.autoplay = true; video.muted = true; video.loop = true;
      video.setAttribute('playsinline', ''); video.preload = 'auto';
      video.addEventListener('loadeddata', function() { this.classList.add('is-ready'); });
      video.addEventListener('error', function() { this.style.display = 'none'; });
      var source = document.createElement('source');
      source.src = data.videoUrl; source.type = 'video/mp4';
      video.appendChild(source);
      container.appendChild(video);
    } else if (data.featuredImage) {
      container.className = '';
      container.textContent = '';
      var img = document.createElement('img');
      img.src = data.featuredImage; img.alt = ''; img.loading = 'eager';
      img.style.cssText = 'position:absolute;inset:0;width:100%;height:100%;object-fit:cover;';
      container.appendChild(img);
    } else {
      container.className = 'product-gradient-placeholder';
      container.textContent = '';
    }
  }

  /* ── Update PWYC bar price ── */
  function updatePWYCBar(data) {
    var barPrice = document.getElementById('pwyc-trigger-price');
    if (barPrice && state.selectedPriceFormatted) {
      barPrice.textContent = state.selectedPriceFormatted;
    }
  }

  /* ── Rebuild PWYC modal dropdown with new variants ── */
  function updatePWYCModal(data) {
    var variants = data.variants || [];

    /* Update dropdown trigger text */
    var triggerText = document.getElementById('pwyc-dropdown-text');
    if (triggerText && state.selectedPriceFormatted) {
      triggerText.textContent = dropdownLabel(state.selectedPriceFormatted, state.selectedVariantId);
    }

    /* Rebuild dropdown options */
    var panel = document.getElementById('pwyc-dropdown-panel');
    if (panel) {
      var html = '';
      for (var i = 0; i < variants.length; i++) {
        var v = variants[i];
        var isRetail = v.isRetail || v.id === data.retailVariantId;
        var isActive = v.id === state.selectedVariantId;
        var classes = 'pwyc-dropdown__option';
        if (isRetail) classes += ' retail';
        if (isActive) classes += ' active';

        html += '<button class="' + classes + '" type="button"'
          + ' role="option"'
          + ' aria-selected="' + (isActive ? 'true' : 'false') + '"'
          + ' tabindex="' + (isActive ? '0' : '-1') + '"'
          + ' data-variant-id="' + v.id + '"'
          + ' data-price="' + v.price + '"'
          + ' data-price-formatted="' + (v.priceFormattedWithCents || v.priceFormatted) + '"'
          + (v.available ? '' : ' disabled')
          + '>'
          + (v.priceFormattedWithCents || v.priceFormatted)
          + (isRetail ? ' (SUGGESTED PRICE)' : '')
          + '</button>';
      }
      panel.innerHTML = html;
    }

    /* Update retail price in modal message */
    var modalRetailStrong = document.querySelector('.pwyc-modal__message strong');
    if (modalRetailStrong && data.retailPriceFormatted) {
      modalRetailStrong.textContent = data.retailPriceFormatted;
    }

    /* Reset quantity */
    state.quantity = 1;
    var qtyDisplay = document.getElementById('qty-display');
    if (qtyDisplay) qtyDisplay.textContent = '1';
    updateAddToCartText();
  }

  /* ── Rebuild mobile sheet select with new variants ── */
  function updateMobileSheetVariants(data) {
    var select = document.getElementById('pwyc-sheet-price');
    if (!select) return;

    var variants = data.variants || [];
    var html = '';
    for (var i = 0; i < variants.length; i++) {
      var v = variants[i];
      var isRetail = v.isRetail || v.id === data.retailVariantId;
      var isSelected = v.id === state.selectedVariantId;

      html += '<option value="' + v.id + '"'
        + (isSelected ? ' selected' : '')
        + (v.available ? '' : ' disabled')
        + '>'
        + (v.priceFormattedWithCents || v.priceFormatted)
        + (isRetail ? ' (Suggested)' : '')
        + '</option>';
    }
    select.innerHTML = html;

    /* Update retail price reference in sheet */
    var sheetRetail = document.getElementById('sheet-retail');
    if (sheetRetail && data.retailPriceFormatted) {
      sheetRetail.textContent = data.retailPriceFormatted;
    }

  }

  /* ── Toggle active class on format thumbnails ── */
  function updateFormatChooser(handle) {
    var options = document.querySelectorAll('.product-chooser [data-format-handle]');
    for (var i = 0; i < options.length; i++) {
      var optHandle = options[i].getAttribute('data-format-handle');
      if (optHandle === handle) {
        options[i].classList.add('active');
      } else {
        options[i].classList.remove('active');
      }
    }
  }

  /* ── Push new URL to history ── */
  /* Switching format swaps the product (candle <-> wax melt). pushState moved
     the address bar and the title and left everything ELSE on the page still
     describing the product the visitor navigated away from:

       - <link rel="canonical"> pointed at the old URL
       - the share button shared the old title
       - the Product JSON-LD kept the old @id, url, name AND price range, and
         the two formats do not share a price range at all ($18 to $33 against
         $7 to $11), so it advertised the wrong price to crawlers

     Canonical and the share button can be corrected exactly. The JSON-LD
     cannot: rebuilding an accurate offers block client-side needs price data
     this handler is never given. Structured data stating the wrong price is
     worse than none, so it is REMOVED on switch; the server-rendered block
     comes back on the next real page load. */
  function updateURL(data) {
    if (data.url) {
      /* Preserve query params (e.g. preview_theme_id) */
      var newUrl = data.url;
      if (window.location.search) {
        newUrl += window.location.search;
      }
      history.pushState({ handle: data.handle }, data.title, newUrl);

      var canonical = document.querySelector('link[rel="canonical"]');
      if (canonical) {
        canonical.setAttribute('href', window.location.origin + data.url);
      }
    }
    /* Pipe, not an en dash: no en dashes in customer copy, and this line
       overrides the <title> in theme.liquid that was corrected for exactly
       that. */
    if (data.title) {
      document.title = pwycPageTitle(data.title);
    }

    /* The share button carries its own copy of the title, so it has to be
       re-stamped or it shares the previous product. */
    var shareEl = document.querySelector('[data-share-title]');
    if (shareEl && data.title) {
      shareEl.setAttribute('data-share-title', pwycPageTitle(data.title));
    }

    /* Drop structured data we can no longer vouch for (see the note above). */
    var stale = document.querySelectorAll('script[type="application/ld+json"]');
    for (var i = 0; i < stale.length; i++) {
      if ((stale[i].textContent || '').indexOf('"Product"') !== -1) {
        stale[i].parentNode.removeChild(stale[i]);
      }
    }
  }

  /* One place that builds the page title, so the two callers cannot drift
     apart on the separator again. */
  function pwycPageTitle(title) {
    return title.replace(' Wax Melts', '') + ' | Alatreyu';
  }

  /* ── Clear cost displays while new data loads ── */
  /* ── Refresh "Similar Scents" recommendations for a new product ── */
  function refreshSimilarScents(productId) {
    var recEl = document.querySelector('product-recommendations');
    if (!recEl || !productId) return;

    var sectionId = recEl.getAttribute('data-section-id');
    var baseUrl = recEl.getAttribute('data-url');
    if (!sectionId || !baseUrl) return;

    /* Build the section rendering URL for the new product */
    var url = baseUrl
      + '&product_id=' + productId
      + '&section_id=' + sectionId;

    fetch(url)
      .then(function (res) { return res.text(); })
      .then(function (html) {
        /* Parse the response and extract the section content */
        var tmp = document.createElement('div');
        tmp.innerHTML = html;
        var newSection = tmp.querySelector('.alatreyu-recommended');
        if (newSection) {
          recEl.innerHTML = newSection.innerHTML;
          /* Update data-product-id so future reloads use the right product */
          recEl.setAttribute('data-product-id', productId);
        }
      })
      .catch(function () { /* silent — recommendations are non-critical */ });
  }

  function clearCostData() {
    var modalCost = document.getElementById('modal-cost-to-make');
    if (modalCost) modalCost.textContent = '...';

    var modalLines = document.getElementById('modal-popup-cost-lines');
    if (modalLines) modalLines.innerHTML = '<p class="cost-popup__text">Loading...</p>';

    var modalPopup = document.getElementById('modal-cost-popup');
    if (modalPopup) {
      var totalEl = modalPopup.querySelector('.cost-total');
      if (totalEl) totalEl.innerHTML = '<span>Total Cost</span><span>...</span>';
    }

    /* Clear mobile sheet cost toggle + expandable detail */
    var sheetCostToggle = document.getElementById('sheet-cost-toggle');
    if (sheetCostToggle) sheetCostToggle.textContent = '...';

    var sheetDetail = document.getElementById('sheet-cost-detail');
    if (sheetDetail) {
      var detailLines = sheetDetail.querySelectorAll('.pwyc-sheet__cost-line');
      for (var i = 0; i < detailLines.length; i++) detailLines[i].remove();
      var detailTotal = sheetDetail.querySelector('.pwyc-sheet__cost-total');
      if (detailTotal) detailTotal.innerHTML = '<span>Total Cost</span><span>...</span>';
    }

    /* Clear standalone cost modal */
    var costModal = document.getElementById('cost-modal');
    if (costModal) {
      var lines = costModal.querySelectorAll('.cost-line');
      for (var i = 0; i < lines.length; i++) {
        lines[i].remove();
      }
      var totalLine = costModal.querySelector('.cost-total');
      if (totalLine) totalLine.innerHTML = '<span>Total Cost to Make</span><span>...</span>';
    }
  }

  /* ══════════════════════════════════════════════════════
     EXISTING FUNCTIONALITY (unchanged below)
     ══════════════════════════════════════════════════════ */

  /* ── Fetch live BOM cost breakdown from Odoo via Storefront API ── */
  function fetchLiveCostBreakdown() {
    if (!state.productData || !state.productData.sku) return;

    fetch(STOREFRONT_API + '/api/products/' + encodeURIComponent(state.productData.sku) + '/cost-breakdown')
      .then(function (res) {
        if (!res.ok) return null;
        return res.json();
      })
      .then(function (data) {
        if (!data || !data.lines || data.lines.length === 0) return;
        populateCostBreakdown(data);
      })
      .catch(function () { /* silent — keep Liquid fallback */ });
  }

  /* ── Populate cost data into PWYC modal + sheet + cost modal ── */
  function populateCostBreakdown(data) {
    var total = data.total;
    var lines = data.lines;
    for (var _fn = 0; _fn < lines.length; _fn++) {
      var _nm = lines[_fn].name || '';
      if (_nm.indexOf('Soy Wax') !== -1) { lines[_fn].name = 'Coconut & Soy Wax'; }
      else if (_nm.indexOf('Wick') !== -1) { lines[_fn].name = 'Candle Wick'; }
    }

    /* Update total sold from Odoo (if API provided it) */
    if (data.totalSold !== undefined && data.totalSold > 0) {
      var soldEl = document.querySelector('.product-chooser__sold');
      if (soldEl) {
        soldEl.textContent = '\u00b7 ' + Number(data.totalSold).toLocaleString() + ' sold';
        soldEl.style.display = '';
      }
      if (state.productData) state.productData.totalSold = data.totalSold;
    }

    /* Build cost lines HTML */
    var linesHtml = '';
    for (var i = 0; i < lines.length; i++) {
      linesHtml += '<div class="cost-line">'
        + '<span class="cost-line__name">' + escapeHtml(lines[i].name) + ' <small>(' + escapeHtml(lines[i].qty) + ')</small></span>'
        + '<span class="cost-line__amount">$' + lines[i].cost.toFixed(2) + '</span>'
        + '</div>';
    }

    /* Update "Cost to Make" value in PWYC modal */
    var modalCost = document.getElementById('modal-cost-to-make');
    if (modalCost) modalCost.textContent = '$' + total.toFixed(2);

    /* Update cost popup lines in PWYC modal */
    var modalLines = document.getElementById('modal-popup-cost-lines');
    if (modalLines) modalLines.innerHTML = linesHtml;

    /* Update cost popup total in PWYC modal */
    var modalPopup = document.getElementById('modal-cost-popup');
    if (modalPopup) {
      var totalEl = modalPopup.querySelector('.cost-total');
      if (totalEl) {
        totalEl.innerHTML = '<span>Total Cost</span><span>$' + total.toFixed(2) + '</span>';
      }
    }

    /* Update mobile sheet cost toggle + expandable detail */
    var sheetCostToggle = document.getElementById('sheet-cost-toggle');
    if (sheetCostToggle) sheetCostToggle.textContent = '$' + total.toFixed(2);

    var sheetDetail = document.getElementById('sheet-cost-detail');
    if (sheetDetail) {
      /* Remove old lines */
      var oldDetailLines = sheetDetail.querySelectorAll('.pwyc-sheet__cost-line');
      for (var d = 0; d < oldDetailLines.length; d++) oldDetailLines[d].remove();
      /* Build new lines before the total */
      var detailTotal = sheetDetail.querySelector('.pwyc-sheet__cost-total');
      for (var e = 0; e < lines.length; e++) {
        var detailDiv = document.createElement('div');
        detailDiv.className = 'pwyc-sheet__cost-line';
        detailDiv.innerHTML = '<span>' + escapeHtml(lines[e].name) + ' <small>(' + escapeHtml(lines[e].qty) + ')</small></span>'
          + '<span>$' + lines[e].cost.toFixed(2) + '</span>';
        if (detailTotal) {
          sheetDetail.insertBefore(detailDiv, detailTotal);
        } else {
          sheetDetail.appendChild(detailDiv);
        }
      }
      /* Update total */
      if (detailTotal) detailTotal.innerHTML = '<span>Total Cost</span><span>$' + total.toFixed(2) + '</span>';
    }

    /* Update standalone cost transparency modal (cost-breakdown.liquid) */
    var costModalLines = document.querySelector('.alatreyu-cost-modal__lines');
    if (costModalLines) {
      var modalLinesHtml = '';
      for (var j = 0; j < lines.length; j++) {
        modalLinesHtml += '<div class="alatreyu-cost-modal__line">'
          + '<span class="alatreyu-cost-modal__line-name">' + escapeHtml(lines[j].name)
          + (lines[j].qty ? ' <span class="alatreyu-cost-modal__line-qty">' + escapeHtml(lines[j].qty) + '</span>' : '')
          + '</span>'
          + '<span class="alatreyu-cost-modal__line-cost">$' + lines[j].cost.toFixed(2) + '</span>'
          + '</div>';
      }
      costModalLines.innerHTML = modalLinesHtml;
    }

    /* Update standalone cost modal lines + total */
    var costModal = document.getElementById('cost-modal');
    if (costModal) {
      var innerEl = costModal.querySelector('.cost-modal__inner');
      if (innerEl) {
        /* Remove old cost lines */
        var oldLines = innerEl.querySelectorAll('.cost-line');
        for (var k = 0; k < oldLines.length; k++) {
          oldLines[k].remove();
        }
        /* Insert new cost lines before the total */
        var totalLine = innerEl.querySelector('.cost-total');
        if (totalLine) {
          for (var m = 0; m < lines.length; m++) {
            var lineDiv = document.createElement('div');
            lineDiv.className = 'cost-line';
            lineDiv.innerHTML =
              '<span class="cost-line__name">' + escapeHtml(lines[m].name) + ' <small>(' + escapeHtml(lines[m].qty) + ')</small></span>'
              + '<span class="cost-line__amount">$' + lines[m].cost.toFixed(2) + '</span>';
            innerEl.insertBefore(lineDiv, totalLine);
          }
          totalLine.innerHTML = '<span>Total Cost to Make</span><span>$' + total.toFixed(2) + '</span>';
        }
      }
    }
  }

  /* ── PWYC Trigger (price button on bar) → open modal ── */
  var releasePWYCTrap = null;
  var releaseSheetTrap = null;

  function initPWYCTrigger() {
    var trigger = document.getElementById('pwyc-trigger');
    var triggerArea = document.getElementById('pwyc-trigger-area');
    if (!trigger && !triggerArea) return;

    function openModal() {
      var modal = document.getElementById('pwyc-modal');
      var sheet = document.getElementById('pwyc-sheet');
      /* Mobile → bottom sheet; Desktop → modal */
      if (window.innerWidth <= 768 && sheet) {
        sheet.classList.add('open');
        document.body.style.overflow = 'hidden';
        releaseSheetTrap = trapFocus(sheet);
        var sheetClose = sheet.querySelector('.pwyc-sheet__close');
        if (sheetClose) sheetClose.focus();
      } else if (modal) {
        /* Sort dropdown options descending before showing */
        sortDropdownOptions();
        modal.classList.add('open');
        document.body.style.overflow = 'hidden';
        releasePWYCTrap = trapFocus(modal);
        var modalClose = document.getElementById('pwyc-modal-close');
        if (modalClose) modalClose.focus();
      }
    }

    if (trigger) trigger.addEventListener('click', openModal);
    if (triggerArea) triggerArea.addEventListener('click', openModal);

    // Auto-open the pay-what-you-can modal when arriving from a back-in-stock
    // email link (?pwyc=open), but only if the product is actually available
    // (a sold-out product hides the picker and shows the Notify card instead).
    try {
      var _pwyc = new URLSearchParams(window.location.search).get('pwyc');
      if (_pwyc && _pwyc !== '0' && _pwyc !== 'false') {
        var _actions = document.querySelector('.pwyc-bar__actions');
        var _avail = !_actions || _actions.getAttribute('data-available') === 'true';
        if (_avail) {
          openModal();
        }
      }
    } catch (e) { /* URLSearchParams unsupported or no modal — ignore */ }
  }

  /* ── Sort dropdown options descending (highest price first) ── */
  function sortDropdownOptions() {
    var panel = document.getElementById('pwyc-dropdown-panel');
    if (!panel) return;

    var options = Array.prototype.slice.call(panel.querySelectorAll('.pwyc-dropdown__option'));
    options.sort(function (a, b) {
      var priceA = parseInt(a.getAttribute('data-price'), 10) || 0;
      var priceB = parseInt(b.getAttribute('data-price'), 10) || 0;
      return priceB - priceA;
    });

    /* Re-append sorted */
    for (var i = 0; i < options.length; i++) {
      panel.appendChild(options[i]);
    }
  }

  /* ── Position dropdown panel to center on active option ── */
  function positionDropdownPanel() {
    var dropdown = document.getElementById('pwyc-dropdown');
    var panel = document.getElementById('pwyc-dropdown-panel');
    var trigger = document.getElementById('pwyc-dropdown-trigger');
    if (!dropdown || !panel || !trigger) return;

    var activeOpt = panel.querySelector('.pwyc-dropdown__option.active');
    if (!activeOpt) return;

    /* Reset to measure cleanly */
    panel.style.top = '0px';
    panel.scrollTop = 0;

    var triggerHeight = trigger.offsetHeight;
    var panelHeight = panel.offsetHeight;

    /* Center panel vertically on trigger button */
    var desiredTop = (triggerHeight - panelHeight) / 2;

    /* Clamp: don't extend above modal inner */
    var modalInner = dropdown.closest('.pwyc-modal__inner');
    if (modalInner) {
      var modalInnerRect = modalInner.getBoundingClientRect();
      var dropdownRect = dropdown.getBoundingClientRect();
      var maxUp = -(dropdownRect.top - modalInnerRect.top - 10);
      desiredTop = Math.max(desiredTop, maxUp);
    }

    panel.style.top = desiredTop + 'px';

    /* Scroll to center the active option */
    requestAnimationFrame(function () {
      var panelClientHeight = panel.clientHeight;
      var scrollTarget = activeOpt.offsetTop - (panelClientHeight / 2) + (activeOpt.offsetHeight / 2);
      panel.scrollTop = Math.max(0, scrollTarget);
    });
  }

  /* ── PWYC Modal open/close ── */
  function initPWYCModal() {
    var modal = document.getElementById('pwyc-modal');
    if (!modal) return;

    var closeBtn = document.getElementById('pwyc-modal-close');
    var confirmBtn = document.getElementById('pwyc-modal-confirm');

    function closeModal() {
      modal.classList.remove('open');
      document.body.style.overflow = '';
      if (releasePWYCTrap) { releasePWYCTrap(); releasePWYCTrap = null; }
      /* Also close the dropdown and popups */
      var dropdown = document.getElementById('pwyc-dropdown');
      if (dropdown) {
        dropdown.classList.remove('open');
        var ddTrigger = document.getElementById('pwyc-dropdown-trigger');
        if (ddTrigger) ddTrigger.setAttribute('aria-expanded', 'false');
      }
      var popups = modal.querySelectorAll('.cost-popup.open');
      for (var i = 0; i < popups.length; i++) {
        popups[i].classList.remove('open');
      }
    }

    /* Close on backdrop click */
    modal.addEventListener('click', function (e) {
      if (e.target === modal) closeModal();
    });

    if (closeBtn) closeBtn.addEventListener('click', closeModal);

    /* Escape key */
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && modal.classList.contains('open')) {
        closeModal();
      }
    });

    /* Confirm → add to cart, close modal, and update bar price */
    if (confirmBtn) {
      confirmBtn.addEventListener('click', function () {
        if (!state.selectedVariantId) return;
        updateBarPrice();

        var originalText = confirmBtn.textContent;
        var lockedWidth = confirmBtn.offsetWidth;
        confirmBtn.style.width = lockedWidth + 'px';
        confirmBtn.disabled = true;
        confirmBtn.textContent = 'Adding...';

        fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: [{ id: state.selectedVariantId, quantity: state.quantity }] })
        })
        .then(function (res) {
          /* fetch() only rejects on a NETWORK error. A Shopify 422 -- the variant is
             sold out under a DENY inventory policy -- resolves normally, so res.json()
             parsed the ERROR body and the success branch below ran on it: success
             haptic, button reset, modal closed, cart drawer opened on a cart that does
             not contain the item, and a pwyc_add_to_cart fired for an add that never
             happened.
             
             Verified against the live store 2026-08-24: POST /cart/add.js for a
             sold-out variant returns 422 with "... is already sold out." At that moment
             27 of 34 products were fully sold out, so this was the COMMON path, not an
             edge case, and every phantom event replaced a real stockout-demand signal
             in GA4 with a normal-looking add. */
          if (!res.ok) {
            return res.json().catch(function () { return {}; }).then(function (err) {
              throw new Error(err.description || err.message || 'Could not add to cart');
            });
          }
          return res.json();
        })
        .then(function () {
          if (window.AlatreyuHaptics) window.AlatreyuHaptics('success');
          confirmBtn.textContent = originalText;
          confirmBtn.style.width = '';
          confirmBtn.disabled = false;
          closeModal();
          updateCartCount();
          if (window.AlatreyuCartDrawer) window.AlatreyuCartDrawer.openAfterRefresh();
        })
        .catch(function () {
          if (window.AlatreyuHaptics) window.AlatreyuHaptics('error');
          confirmBtn.textContent = 'Error';
          setTimeout(function () {
            confirmBtn.textContent = originalText;
            confirmBtn.style.width = '';
            confirmBtn.disabled = false;
          }, 2000);
        });
      });
    }
  }

  /* ── Custom Dropdown ── */
  function initPWYCDropdown() {
    var dropdown = document.getElementById('pwyc-dropdown');
    if (!dropdown) return;

    var trigger = document.getElementById('pwyc-dropdown-trigger');
    var panel = document.getElementById('pwyc-dropdown-panel');
    var triggerText = document.getElementById('pwyc-dropdown-text');

    function openDropdown() {
      dropdown.classList.add('open');
      if (trigger) trigger.setAttribute('aria-expanded', 'true');
      requestAnimationFrame(function () {
        positionDropdownPanel();
        /* Focus the active option — use a second rAF to ensure
           the panel is visible and positioned before focusing */
        requestAnimationFrame(function () {
          var active = panel && panel.querySelector('.pwyc-dropdown__option.active');
          if (active) {
            active.setAttribute('tabindex', '0');
            active.focus();
          }
        });
      });
    }

    function closeDropdown() {
      dropdown.classList.remove('open');
      if (trigger) {
        trigger.setAttribute('aria-expanded', 'false');
        trigger.focus();
      }
    }

    function selectOption(option) {
      if (!option || option.disabled) return;

      /* Remove active/aria-selected from all, set on selected.
         Use roving tabindex: active option gets tabindex="0",
         all others get tabindex="-1" so only the active one
         is reachable via Tab. */
      var all = panel.querySelectorAll('.pwyc-dropdown__option');
      for (var i = 0; i < all.length; i++) {
        all[i].classList.remove('active');
        all[i].setAttribute('aria-selected', 'false');
        all[i].setAttribute('tabindex', '-1');
      }
      option.classList.add('active');
      option.setAttribute('aria-selected', 'true');
      option.setAttribute('tabindex', '0');
      if (window.AlatreyuHaptics) window.AlatreyuHaptics('tap');

      /* Update state */
      var variantId = parseInt(option.getAttribute('data-variant-id'), 10);
      state.selectedVariantId = variantId;
      state.selectedPriceFormatted = option.getAttribute('data-price-formatted') || option.getAttribute('data-price');
      state.selectedPrice = option.getAttribute('data-price');
      state.tierChangeCount++;

      /* Publish PWYC tier selection for Custom Pixel analytics */
      if (typeof Shopify !== 'undefined' && Shopify.analytics && Shopify.analytics.publish) {
        Shopify.analytics.publish('pwyc_tier_selected', {
          product_handle: state.currentHandle || (state.productData && state.productData.handle) || '',
          variant_id: variantId,
          tier_price: state.selectedPrice,
          tier_label: state.selectedPriceFormatted
        });
      }
      /* GA4 pwyc_tier_selected event (with behavior context) */
      if (typeof gtag === 'function') {
        gtag('event', 'pwyc_tier_selected', {
          product_handle: state.currentHandle || (state.productData && state.productData.handle) || '',
          tier_price: state.selectedPrice,
          tier_label: state.selectedPriceFormatted,
          tier_change_count: state.tierChangeCount,
          seconds_on_page: Math.round((Date.now() - state.pageLoadTime) / 1000),
          saw_cost_breakdown: state.sawCostBreakdown
        });
      }

      /* Sync express checkout form with selected variant */
      document.dispatchEvent(new CustomEvent('alatreyu:variant-change', { detail: { variantId: variantId } }));

      /* Update trigger text with retail label */
      if (triggerText) {
        triggerText.textContent = dropdownLabel(state.selectedPriceFormatted, variantId);
      }

      /* Close dropdown */
      closeDropdown();
    }

    if (trigger) {
      trigger.addEventListener('click', function (e) {
        e.stopPropagation();
        if (dropdown.classList.contains('open')) {
          closeDropdown();
        } else {
          openDropdown();
        }
      });

      /* Keyboard handler on trigger — when dropdown is open and focus
         stays on the trigger (common scenario), delegate arrow key
         navigation to the panel options so keyboard nav still works */
      trigger.addEventListener('keydown', function (e) {
        if (!dropdown.classList.contains('open')) return;

        var options = panel ? Array.prototype.slice.call(panel.querySelectorAll('.pwyc-dropdown__option:not([disabled])')) : [];
        if (!options.length) return;

        if (e.key === 'ArrowDown') {
          e.preventDefault();
          /* Focus the active option, or the first one */
          var active = panel.querySelector('.pwyc-dropdown__option.active') || options[0];
          var idx = options.indexOf(active);
          /* If active is focused already, move to next; otherwise focus the active one */
          if (document.activeElement === active && idx < options.length - 1) {
            options[idx + 1].focus();
          } else {
            active.focus();
          }
        } else if (e.key === 'ArrowUp') {
          e.preventDefault();
          var active = panel.querySelector('.pwyc-dropdown__option.active') || options[options.length - 1];
          var idx = options.indexOf(active);
          if (document.activeElement === active && idx > 0) {
            options[idx - 1].focus();
          } else {
            active.focus();
          }
        } else if (e.key === 'Home') {
          e.preventDefault();
          options[0].focus();
        } else if (e.key === 'End') {
          e.preventDefault();
          options[options.length - 1].focus();
        } else if (e.key === 'Enter' || e.key === ' ') {
          /* If dropdown is open and focus is on trigger, select the active option */
          e.preventDefault();
          var active = panel.querySelector('.pwyc-dropdown__option.active');
          if (active) selectOption(active);
        } else if (e.key === 'Escape') {
          e.preventDefault();
          closeDropdown();
        }
      });
    }

    /* Option click — use event delegation so dynamically replaced options still work */
    if (panel) {
      panel.addEventListener('click', function (e) {
        var option = e.target.closest('.pwyc-dropdown__option');
        selectOption(option);
      });

      /* Keyboard navigation for listbox (roving tabindex) */
      panel.addEventListener('keydown', function (e) {
        var options = Array.prototype.slice.call(panel.querySelectorAll('.pwyc-dropdown__option:not([disabled])'));
        var current = document.activeElement;
        var idx = options.indexOf(current);

        function focusOption(targetIdx) {
          /* Update roving tabindex: old option loses tab stop, new one gains it */
          if (idx >= 0 && idx < options.length) {
            options[idx].setAttribute('tabindex', '-1');
          }
          options[targetIdx].setAttribute('tabindex', '0');
          options[targetIdx].focus();
        }

        if (e.key === 'ArrowDown') {
          e.preventDefault();
          var next = idx < options.length - 1 ? idx + 1 : 0;
          focusOption(next);
        } else if (e.key === 'ArrowUp') {
          e.preventDefault();
          var prev = idx > 0 ? idx - 1 : options.length - 1;
          focusOption(prev);
        } else if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          selectOption(current);
        } else if (e.key === 'Escape') {
          e.preventDefault();
          closeDropdown();
        } else if (e.key === 'Home') {
          e.preventDefault();
          focusOption(0);
        } else if (e.key === 'End') {
          e.preventDefault();
          focusOption(options.length - 1);
        }
      });
    }

    /* Close dropdown on outside click (but within modal) */
    var modal = document.getElementById('pwyc-modal');
    if (modal) {
      modal.addEventListener('click', function (e) {
        if (!dropdown.contains(e.target)) {
          closeDropdown();
        }
      });
    }
  }

  /* ── Quantity Selector ── */
  function initQtySelector() {
    var display = document.getElementById('qty-display');
    var buttons = document.querySelectorAll('[data-qty-action]');

    for (var i = 0; i < buttons.length; i++) {
      buttons[i].addEventListener('click', function () {
        var action = this.getAttribute('data-qty-action');
        if (action === 'increase' && state.quantity < 10) {
          state.quantity++;
        } else if (action === 'decrease' && state.quantity > 1) {
          state.quantity--;
        }
        if (display) display.textContent = state.quantity;
        if (window.AlatreyuHaptics) window.AlatreyuHaptics('tap');
        updateAddToCartText();
      });
    }
  }

  /* ── Update Add to Cart button text based on qty ── */
  function updateAddToCartText() {
    var barBtn = document.getElementById('add-to-cart-btn');
    if (!barBtn) return;
    var desktopSpan = barBtn.querySelector('.btn-desktop');
    if (desktopSpan) {
      desktopSpan.textContent = state.quantity > 1
        ? 'Add ' + state.quantity + ' to Cart'
        : 'Add to Cart';
    }
  }

  /* ── Add to Cart (bar button + sheet button) ── */
  function initAddToCart() {
    var barBtn = document.getElementById('add-to-cart-btn');
    var sheetBtn = document.getElementById('pwyc-sheet-add');

    function addToCart(variantId, qty) {
      if (!variantId) return;

      var body = JSON.stringify({
        items: [{
          id: variantId,
          quantity: qty
        }]
      });

      return fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: body
      })
      .then(function (res) {
        /* fetch() only rejects on a NETWORK error. A Shopify 422 -- the variant is
           sold out under a DENY inventory policy -- resolves normally, so res.json()
           parsed the ERROR body and the success branch below ran on it: success
           haptic, button reset, modal closed, cart drawer opened on a cart that does
           not contain the item, and a pwyc_add_to_cart fired for an add that never
           happened.
           
           Verified against the live store 2026-08-24: POST /cart/add.js for a
           sold-out variant returns 422 with "... is already sold out." At that moment
           27 of 34 products were fully sold out, so this was the COMMON path, not an
           edge case, and every phantom event replaced a real stockout-demand signal
           in GA4 with a normal-looking add. */
        if (!res.ok) {
          return res.json().catch(function () { return {}; }).then(function (err) {
            throw new Error(err.description || err.message || 'Could not add to cart');
          });
        }
        return res.json();
      })
      .then(function (data) {
        updateCartCount();
        /* Publish add to cart for Custom Pixel analytics */
        if (typeof Shopify !== 'undefined' && Shopify.analytics && Shopify.analytics.publish) {
          Shopify.analytics.publish('pwyc_add_to_cart', {
            product_handle: state.currentHandle || (state.productData && state.productData.handle) || '',
            variant_id: variantId,
            quantity: qty,
            tier_price: state.selectedPrice
          });
        }
        /* GA4 pwyc_add_to_cart event (with PWYC tier context)
           Renamed from add_to_cart to avoid duplicating Shopify's built-in pixel */
        if (typeof gtag === 'function' && state.productData) {
          var selVariant = null;
          var variants = state.productData.variants || [];
          for (var i = 0; i < variants.length; i++) {
            if (variants[i].id === variantId) { selVariant = variants[i]; break; }
          }
          var itemPrice = selVariant ? selVariant.price / 100 : 0;
          gtag('event', 'pwyc_add_to_cart', {
            tier_price: state.selectedPrice || '',
            tier_label: state.selectedPriceFormatted || '',
            tier_change_count: state.tierChangeCount,
            seconds_to_add: Math.round((Date.now() - state.pageLoadTime) / 1000),
            saw_cost_breakdown: state.sawCostBreakdown,
            currency: 'USD',
            value: itemPrice * qty,
            items: [{
              item_id: state.productData.sku || '',
              item_name: state.productData.title || '',
              item_brand: 'Alatreyu',
              price: itemPrice,
              quantity: qty
            }]
          });
        }
        return data;
      });
    }

    function handleAdd(btn) {
      if (!btn) return;
      btn.addEventListener('click', function () {
        if (window.AlatreyuHaptics) window.AlatreyuHaptics('tap');
        /* Save original span text for restoration */
        var desktopSpan = btn.querySelector('.btn-desktop');
        var mobileSpan = btn.querySelector('.btn-mobile');
        var originalDesktop = desktopSpan ? desktopSpan.textContent : '';
        var originalMobile = mobileSpan ? mobileSpan.textContent : '';
        var originalText = btn.textContent;
        var hasDualText = !!desktopSpan;
        var lockedWidth = btn.offsetWidth;
        btn.style.width = lockedWidth + 'px';

        btn.disabled = true;
        if (hasDualText) {
          if (desktopSpan) desktopSpan.textContent = 'Adding...';
          if (mobileSpan) mobileSpan.textContent = 'Adding...';
        } else {
          btn.textContent = 'Adding...';
        }

        addToCart(state.selectedVariantId, state.quantity)
          .then(function () {
            if (window.AlatreyuHaptics) window.AlatreyuHaptics('success');
            /* Restore button text and open cart drawer */
            if (hasDualText) {
              if (desktopSpan) desktopSpan.textContent = originalDesktop;
              if (mobileSpan) mobileSpan.textContent = originalMobile;
            } else {
              btn.textContent = originalText;
            }
            btn.style.width = '';
            btn.disabled = false;
            if (window.AlatreyuCartDrawer) {
              window.AlatreyuCartDrawer.openAfterRefresh();
            }
          })
          .catch(function () {
            if (window.AlatreyuHaptics) window.AlatreyuHaptics('error');
            if (hasDualText) {
              if (desktopSpan) desktopSpan.textContent = 'Error';
              if (mobileSpan) mobileSpan.textContent = 'Error';
            } else {
              btn.textContent = 'Error';
            }
            setTimeout(function () {
              if (hasDualText) {
                if (desktopSpan) desktopSpan.textContent = originalDesktop;
                if (mobileSpan) mobileSpan.textContent = originalMobile;
              } else {
                btn.textContent = originalText;
              }
              btn.style.width = '';
              btn.disabled = false;
            }, 2000);
          });
      });
    }

    handleAdd(barBtn);
    handleAdd(sheetBtn);
  }

  /* ── Update bar price display ── */
  function updateBarPrice() {
    var barPrice = document.getElementById('pwyc-trigger-price');
    if (barPrice && state.selectedPriceFormatted) {
      barPrice.textContent = state.selectedPriceFormatted;
    }
  }

  /* ── Update cart count in header ── */
  function updateCartCount() {
    fetch('/cart.js')
      .then(function (res) { return res.json(); })
      .then(function (cart) {
        var badges = document.querySelectorAll('[data-cart-count]');
        for (var i = 0; i < badges.length; i++) {
          badges[i].textContent = cart.item_count;
        }
      })
      .catch(function () { /* silent */ });
  }

  /* ── Cost Transparency Modal ── */
  var releaseCostTrap = null;

  function initCostModal() {
    var modal = document.getElementById('cost-modal');
    if (!modal) return;

    var triggers = document.querySelectorAll('[data-open-cost-modal]');
    var closeBtn = modal.querySelector('.cost-modal__close');

    function openCost() {
      modal.classList.add('open');
      releaseCostTrap = trapFocus(modal);
      if (closeBtn) closeBtn.focus();
      /* Publish cost modal open for Custom Pixel analytics */
      if (typeof Shopify !== 'undefined' && Shopify.analytics && Shopify.analytics.publish) {
        Shopify.analytics.publish('cost_transparency_opened', {
          product_handle: state.currentHandle || (state.productData && state.productData.handle) || ''
        });
      }
      state.sawCostBreakdown = true;
      /* GA4 cost_transparency_opened event */
      if (typeof gtag === 'function') {
        gtag('event', 'cost_transparency_opened', {
          product_handle: state.currentHandle || (state.productData && state.productData.handle) || '',
          seconds_on_page: Math.round((Date.now() - state.pageLoadTime) / 1000)
        });
      }
      /* Stagger cost lines */
      var lines = modal.querySelectorAll('.cost-line');
      for (var i = 0; i < lines.length; i++) {
        lines[i].style.opacity = '0';
        lines[i].style.transform = 'translateY(6px)';
        lines[i].style.transition = 'opacity 300ms ease, transform 300ms ease';
        lines[i].style.transitionDelay = (100 + i * 80) + 'ms';
      }
      /* Trigger reflow then animate */
      requestAnimationFrame(function () {
        for (var j = 0; j < lines.length; j++) {
          lines[j].style.opacity = '1';
          lines[j].style.transform = 'translateY(0)';
        }
      });
    }

    function closeCost() {
      modal.classList.remove('open');
      if (releaseCostTrap) { releaseCostTrap(); releaseCostTrap = null; }
    }

    for (var i = 0; i < triggers.length; i++) {
      triggers[i].addEventListener('click', function (e) {
        e.preventDefault();
        openCost();
      });
    }

    if (closeBtn) closeBtn.addEventListener('click', closeCost);

    modal.addEventListener('click', function (e) {
      if (e.target === modal) closeCost();
    });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && modal.classList.contains('open')) {
        closeCost();
      }
    });
  }

  /* ── Cost Popups in PWYC Modal (hover/click tooltips) ── */
  function initCostPopups() {
    var triggers = document.querySelectorAll('.pwyc-modal__cost-trigger');
    for (var i = 0; i < triggers.length; i++) {
      (function (trigger) {
        var popup = trigger.parentElement.querySelector('.cost-popup');
        if (!popup) return;

        trigger.addEventListener('mouseenter', function () {
          popup.classList.add('open');
        });
        trigger.parentElement.addEventListener('mouseleave', function () {
          popup.classList.remove('open');
        });
        trigger.addEventListener('click', function () {
          popup.classList.toggle('open');
        });
      })(triggers[i]);
    }
  }

  /* ── Mobile Bottom Sheet ── */
  function initMobileSheet() {
    var sheet = document.getElementById('pwyc-sheet');
    if (!sheet) return;

    var select = document.getElementById('pwyc-sheet-price');

    /* Update state when sheet select changes */
    if (select) {
      select.addEventListener('change', function () {
        state.selectedVariantId = parseInt(this.value, 10);
        /* Find the formatted price */
        if (state.productData) {
          var variants = state.productData.variants;
          for (var i = 0; i < variants.length; i++) {
            if (variants[i].id === state.selectedVariantId) {
              state.selectedPriceFormatted = variants[i].priceFormatted;
              state.selectedPrice = variants[i].price;
              break;
            }
          }
        }
        state.tierChangeCount++;
        /* GA4 pwyc_tier_selected event (mobile sheet, with behavior context) */
        if (typeof gtag === 'function') {
          gtag('event', 'pwyc_tier_selected', {
            product_handle: state.currentHandle || (state.productData && state.productData.handle) || '',
            tier_price: state.selectedPrice,
            tier_label: state.selectedPriceFormatted,
            tier_change_count: state.tierChangeCount,
            seconds_on_page: Math.round((Date.now() - state.pageLoadTime) / 1000),
            saw_cost_breakdown: state.sawCostBreakdown
          });
        }
        /* Sync express checkout form */
        document.dispatchEvent(new CustomEvent('alatreyu:variant-change', { detail: { variantId: state.selectedVariantId } }));
      });
    }

    /* Close button (x) */
    var sheetClose = document.getElementById('pwyc-sheet-close');
    function closeSheet() {
      sheet.classList.remove('open');
      document.body.style.overflow = '';
      if (releaseSheetTrap) { releaseSheetTrap(); releaseSheetTrap = null; }
    }
    if (sheetClose) {
      sheetClose.addEventListener('click', closeSheet);
    }

    /* Cost breakdown toggle (mobile sheet) */
    var costToggle = document.getElementById('sheet-cost-toggle');
    var costDetail = document.getElementById('sheet-cost-detail');
    if (costToggle && costDetail) {
      costToggle.addEventListener('click', function () {
        var wasOpen = costDetail.classList.contains('open');
        costDetail.classList.toggle('open');
        if (!wasOpen) {
          state.sawCostBreakdown = true;
          /* GA4 cost_transparency_opened event (mobile) */
          if (typeof gtag === 'function') {
            gtag('event', 'cost_transparency_opened', {
              product_handle: state.currentHandle || (state.productData && state.productData.handle) || '',
              seconds_on_page: Math.round((Date.now() - state.pageLoadTime) / 1000)
            });
          }
        }
      });
    }

    /* Close on backdrop tap (above handle) */
    sheet.addEventListener('click', function (e) {
      if (e.target === sheet) closeSheet();
    });

    /* Close on Escape */
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && sheet.classList.contains('open')) closeSheet();
    });
  }

  /* ── Product Share Button (Web Share API with clipboard fallback) ── */
  function initShareButton() {
    var btn = document.querySelector('.product-share-btn');
    if (!btn) return;

    btn.addEventListener('click', function () {
      var title = btn.getAttribute('data-share-title') || document.title;
      var text = btn.getAttribute('data-share-text') || '';
      var url = btn.getAttribute('data-share-url') || window.location.href;

      if (navigator.share) {
        navigator.share({ title: title, text: text, url: url }).catch(function () {});
      } else {
        navigator.clipboard.writeText(url).then(function () {
          btn.classList.add('copied');
          setTimeout(function () { btn.classList.remove('copied'); }, 2000);
        });
      }

      /* GA4 share event */
      if (typeof gtag === 'function') {
        gtag('event', 'share', {
          method: navigator.share ? 'native' : 'clipboard',
          content_type: 'product',
          item_id: state.currentHandle || ''
        });
      }
    });
  }

  /* Add to init chain */
  document.addEventListener('DOMContentLoaded', function () {
    initShareButton();
  });
})();
